import React, { useState, useEffect, useRef } from 'react';
import { RefreshIcon, TrashIcon } from '../../ui/Icons';
import { UsageDataSource } from '../types';
import { getCacheManager } from '../../../utils/cache-service';

interface ActionsMenuProps {
  dataSource: UsageDataSource;
  loading: boolean;
  isReloading: boolean;
  onReload: () => void;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  dataSource,
  loading,
  isReloading,
  onReload,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void | Promise<void> } | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 3000);
    return () => clearTimeout(timer);
  }, [notification]);

  return (
    <>
    <div className="flex items-center gap-1">
      <button
        onClick={onReload}
        disabled={loading || isReloading}
        className="p-1.5 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        title={isReloading ? "Reloading..." : "Reload data"}
      >
        {isReloading ? (
          <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
        ) : (
          <RefreshIcon className="w-3.5 h-3.5" />
        )}
      </button>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-all"
          title="More actions"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>
        {showMenu && (
          <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-20">
            <div className="py-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setNotification('URL copied!');
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy URL
              </button>

              <div className="border-t border-slate-200 dark:border-slate-600 my-1"></div>

              {dataSource === 'openai' && (
                <button
                  onClick={async () => {
                    try {
                      const cacheManager = getCacheManager();
                      await cacheManager.openai.clearCache();
                      // Legacy localStorage cache keys (kept for backward compatibility).
                      const keys = Object.keys(localStorage);
                      keys.forEach(key => {
                        if (key.startsWith('usage_cache_')) {
                          localStorage.removeItem(key);
                        }
                      });
                      window.location.reload();
                    } catch (error) {
                      console.error('Failed to clear OpenAI cache:', error);
                      setNotification('Failed to clear OpenAI cache');
                    }
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-600 dark:text-orange-400 flex items-center gap-2"
                >
                  <TrashIcon className="w-4 h-4" />
                  Clear Cache
                </button>
              )}

              <button
                onClick={() => {
                  setConfirmAction({
                    title: 'Clear All Storage',
                    message: 'Are you sure you want to clear all storage? This will remove all cached data and settings.',
                    onConfirm: async () => {
                      const cacheManager = getCacheManager();
                      await cacheManager.clearAll();
                      localStorage.clear();
                      window.location.reload();
                    },
                  });
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Clear All Storage
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {confirmAction && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmAction(null)}>
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
          <h3 className="text-white font-medium mb-2">{confirmAction.title}</h3>
          <p className="text-slate-300 text-sm mb-4">{confirmAction.message}</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirmAction(null)} className="px-3 py-1.5 text-sm text-slate-300 hover:text-white">Cancel</button>
            <button
              onClick={() => {
                Promise.resolve(confirmAction.onConfirm())
                  .catch(error => {
                    console.error('Failed to clear storage:', error);
                    setNotification('Failed to clear storage');
                  })
                  .finally(() => setConfirmAction(null));
              }}
              className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    )}

    {notification && (
      <div className="fixed bottom-4 right-4 bg-slate-700 text-white px-4 py-2 rounded-lg shadow-lg text-sm z-50 animate-fade-in">
        {notification}
      </div>
    )}
    </>
  );
};
